import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { applyAnalyticsTools } from "../lib/posthogConsent";

/**
 * Centrální správa souhlasu se cookies.
 *
 * Stav je uložen pod klíčem CONSENT_KEY v localStorage. Klíč samotný spadá
 * pod kategorii "essential" — bez něj bychom nemohli respektovat volbu
 * uživatele, takže ho ukládáme vždy (GDPR čl. 7 odst. 4 — bez něj nelze
 * realizovat výslovně požadovanou službu).
 *
 * Verzování (CONSENT_VERSION) — pokud do budoucna přidáme novou kategorii
 * (např. marketing pixel), zvedneme verzi a banner se uživateli zobrazí znovu,
 * aby měl možnost se aktivně rozhodnout o nové kategorii.
 */
const CONSENT_KEY = "apex_cookie_consent_v1";
const CONSENT_VERSION = 1;
const GOOGLE_ADS_ID = "AW-18426007268";

// Injektuje skript Google Ads (gtag.js) do <head> — voláno POUZE poté, co
// uživatel udělí souhlas s kategorií "marketing" (buď "Přijmout vše", nebo
// zapnutím "Marketing" v Nastavení). Idempotentní — druhé volání nic nedělá.
let googleAdsScriptLoaded = false;
export function loadGoogleAdsTag() {
  if (googleAdsScriptLoaded || typeof document === "undefined") return;
  googleAdsScriptLoaded = true;
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ADS_ID);
}

export const CONSENT_CATEGORIES = [
  {
    id: "essential",
    title: "Nezbytné",
    locked: true,
    description:
      "Bez nich web nefunguje — přihlašovací token, zapamatování této volby. Tyto se vždy ukládají.",
    examples: "apex_token (JWT), apex_cookie_consent_v1",
  },
  {
    id: "functional",
    title: "Funkční",
    locked: false,
    description:
      "Zlepšují vám zážitek — pamatují si, která upozornění v zvonečku jste už zavřeli, a načítají mapu Google s polohou studia.",
    examples: "apex_seen_pay_v1, apex_seen_rejected_v1, Google Maps embed",
  },
  {
    id: "analytics",
    title: "Analytika",
    locked: false,
    description:
      "Anonymně měří, jak je web používán, a pomáhá nám odhalit chyby v ovládání (PostHog — statistiky návštěvnosti a záznam pohybu po stránce). Bez souhlasu se vůbec nespustí.",
    examples: "PostHog (ph_*), záznam relace",
  },
  {
    id: "marketing",
    title: "Marketing",
    locked: false,
    description:
      "Pomáhají nám měřit účinnost reklam (Google Ads — měření konverzí z reklamních kampaní).",
    examples: "Google Ads (gtag.js)",
  },
];

const DEFAULT_DENY = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};
const DEFAULT_ALLOW = {
  essential: true,
  functional: true,
  analytics: true,
  marketing: true,
};

function loadConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(value) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({
        ...value,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      })
    );
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Google Consent Mode v2 — granulární mapování našich kategorií:
 *   analytics  → analytics_storage
 *   marketing  → ad_storage / ad_user_data / ad_personalization
 * Výchozí stav (vše "denied") je nastaven přímo v index.html PŘED gtag.js.
 * Při odmítnutí (nic nepovoleno) se nevolá nic — denied zůstává v platnosti.
 */
export function applyGoogleConsent(value) {
  if (!value) return;
  applyAnalyticsTools(Boolean(value.analytics));
  const analytics = Boolean(value.analytics);
  const marketing = Boolean(value.marketing);
  if (!analytics && !marketing) return;
  if (marketing) loadGoogleAdsTag();
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  gtag("consent", "update", {
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
  });
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => loadConsent());
  // Banner / Settings modal visibility — decoupled from `consent`, so we can
  // re-open the modal even after the user already made a choice.
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // First-load behaviour: if no consent stored, show the banner.
  useEffect(() => {
    if (!consent) {
      // Defer one tick so the page paints first → less jarring
      const t = setTimeout(() => setBannerOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [consent]);

  // Cross-tab sync: when consent is changed in another tab (e.g. user opens
  // the cookie settings in a second window and toggles a category), reflect
  // that change here without requiring a manual reload. The `storage` event
  // only fires in OTHER tabs, so we don't risk feedback loops with our own
  // saveConsent() calls.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== CONSENT_KEY) return;
      if (!e.newValue) {
        // The other tab cleared the consent → start from scratch here too.
        setConsent(null);
        return;
      }
      try {
        const parsed = JSON.parse(e.newValue);
        if (parsed?.version !== CONSENT_VERSION) {
          setConsent(null);
          return;
        }
        setConsent(parsed);
        // If the user revoked functional in the other tab, mirror the purge
        // here so this tab also forgets dismissed notifications etc.
        if (!parsed.functional) purgeNonEssentialStorage();
      } catch {
        setConsent(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Uložený souhlas znovu aplikuj při každém načtení stránky (Consent Mode v2).
  useEffect(() => {
    applyGoogleConsent(loadConsent());
  }, []);

  const acceptAll = useCallback(() => {
    applyGoogleConsent(DEFAULT_ALLOW);
    saveConsent(DEFAULT_ALLOW);
    setConsent({ ...DEFAULT_ALLOW, version: CONSENT_VERSION });
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const acceptEssentialsOnly = useCallback(() => {
    saveConsent(DEFAULT_DENY);
    setConsent({ ...DEFAULT_DENY, version: CONSENT_VERSION });
    // Also purge any previously-stored functional items so we honor the new
    // "essentials only" choice retroactively. This is the bit that makes the
    // banner "really" enforce — not just a UI toggle.
    purgeNonEssentialStorage();
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const setCustomConsent = useCallback((next) => {
    const merged = { ...DEFAULT_DENY, ...next, essential: true };
    applyGoogleConsent(merged);
    saveConsent(merged);
    setConsent({ ...merged, version: CONSENT_VERSION });
    if (!merged.functional) purgeNonEssentialStorage();
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const reopenSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  // Helper used across the app to decide "can I do X?"
  const isAllowed = useCallback(
    (category) => {
      if (category === "essential") return true;
      if (!consent) return false; // strict default — no consent = no extra
      return Boolean(consent[category]);
    },
    [consent]
  );

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      setBannerOpen,
      settingsOpen,
      setSettingsOpen,
      acceptAll,
      acceptEssentialsOnly,
      setCustomConsent,
      reopenSettings,
      isAllowed,
    }),
    [consent, bannerOpen, settingsOpen, acceptAll, acceptEssentialsOnly, setCustomConsent, reopenSettings, isAllowed]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used inside CookieConsentProvider");
  return ctx;
}

// === Storage helpers ===
// All non-essential localStorage keys we control. When user revokes the
// functional category we wipe these so the website behaves as if it was a
// fresh visit (no dismissed notifications etc.).
const FUNCTIONAL_KEYS = ["apex_seen_pay_v1", "apex_seen_rejected_v1"];

export function purgeNonEssentialStorage() {
  try {
    for (const k of FUNCTIONAL_KEYS) localStorage.removeItem(k);
  } catch { /* ignore */ }
}

/**
 * Bezpečné localStorage operace pro funkční (volitelné) položky.
 * Pokud uživatel nesouhlasil s "functional", zápis se zahazuje a čtení vrátí
 * default — komponenty si tak nemusí nic ošetřovat samy.
 */
export function safeSetItem(key, value, category = "functional") {
  try {
    const c = loadConsent();
    if (category === "essential" || c?.[category]) {
      localStorage.setItem(key, value);
    }
  } catch { /* ignore */ }
}

export function safeGetItem(key, fallback = null, category = "functional") {
  try {
    const c = loadConsent();
    if (category === "essential" || c?.[category]) {
      return localStorage.getItem(key) ?? fallback;
    }
  } catch { /* ignore */ }
  return fallback;
}
